/**
 * Production-Safe Console Override
 * Suppresses ALL debug logs in production/staging
 * 
 * Strategy: Nuclear option - suppress everything except errors and warnings
 * 
 * Safety Features:
 * - Respects test environments
 * - Preserves ALL console.error and console.warn calls
 * - Emergency debug mode via localStorage
 * - Try-catch error handling to prevent breaking the app
 * 
 * Usage:
 * - Import at the top of index.js: import './utils/consoleOverride';
 * - Enable debug mode in production: localStorage.setItem('enableDebugLogs', 'true')
 * - Disable debug mode: localStorage.removeItem('enableDebugLogs')
 */

const environment = process.env.REACT_APP_ENVIRONMENT;
const isProduction = environment === 'production';
const isStaging = environment === 'staging';
const isTest = process.env.NODE_ENV === 'test';

/**
 * Check if debug logs should be enabled via localStorage
 * Users can enable this in production for debugging without redeployment
 */
const shouldEnableDebugLogs = () => {
  try {
    return localStorage.getItem('enableDebugLogs') === 'true' ||
           localStorage.getItem('DEBUG') === 'true' ||
           sessionStorage.getItem('enableDebugLogs') === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Setup console override with comprehensive error handling
 */
const setupConsoleOverride = () => {
  // Don't override in test environment (Jest tests need console)
  if (isTest) {
    return;
  }

  // Don't override in development (let developers see everything)
  if (!isProduction && !isStaging) {
    return;
  }

  try {
    // Store original console methods with context binding
    // Using .bind(console) preserves the 'this' context
    const originalConsole = {
      log: console.log.bind(console),
      debug: console.debug.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      // Preserve performance profiling methods
      time: console.time?.bind(console),
      timeEnd: console.timeEnd?.bind(console),
      timeLog: console.timeLog?.bind(console),
    };

    const debugEnabled = shouldEnableDebugLogs();

    // ===== Override console.log - SUPPRESS ALL (unless debug mode) =====
    console.log = function(...args) {
      try {
        // Only show if debug mode is explicitly enabled
        if (debugEnabled) {
          originalConsole.log(...args);
        }
        // Otherwise suppress everything
      } catch (error) {
        // Fallback to original console if override fails
        originalConsole.error('Console override error:', error);
        originalConsole.log(...args);
      }
    };

    // ===== Override console.debug - SUPPRESS ALL (unless debug mode) =====
    console.debug = function(...args) {
      try {
        // Only show if debug mode is explicitly enabled
        if (debugEnabled) {
          originalConsole.debug(...args);
        }
        // Otherwise suppress everything
      } catch (error) {
        originalConsole.error('Console override error:', error);
        originalConsole.debug(...args);
      }
    };

    // ===== Override console.info - SUPPRESS ALL (unless debug mode) =====
    console.info = function(...args) {
      try {
        // Only show if debug mode is explicitly enabled
        if (debugEnabled) {
          originalConsole.info(...args);
        }
        // Otherwise suppress everything
      } catch (error) {
        originalConsole.error('Console override error:', error);
        originalConsole.info(...args);
      }
    };

    // ===== Always preserve warnings and errors =====
    // These should ALWAYS be visible, even in production
    console.warn = originalConsole.warn;
    console.error = originalConsole.error;

    // ===== Preserve performance profiling methods =====
    // Don't interfere with performance.measure, console.time, etc.
    if (originalConsole.time) console.time = originalConsole.time;
    if (originalConsole.timeEnd) console.timeEnd = originalConsole.timeEnd;
    if (originalConsole.timeLog) console.timeLog = originalConsole.timeLog;

    // Override is now active silently
    // No console messages - completely clean output
    // Debug mode can still be enabled via: localStorage.setItem('enableDebugLogs', 'true')

  } catch (error) {
    // If setup completely fails, don't break the app
    // Just log the error and continue without override
    console.error('Failed to setup console override:', error);
    console.warn('Console override disabled - all logs will show normally');
  }
};

// Initialize immediately when this module loads
setupConsoleOverride();

export default setupConsoleOverride;

